import { useCallback, useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { profileClient } from "@/shared/api/clients";
import { ReportGenUser } from "../interfaces/User";
import { useToast } from "../toast";
import { queryClient } from "../api/queryClient";
import { stationUsersQuery, usersKeys } from "../../features/users/api/usersQueries";

export const useStationUsers = (organizationId: string) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [resendingInviteEmail, setResendingInviteEmail] = useState<string | null>(null);
  const [resettingPasswordEmail, setResettingPasswordEmail] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<"activate" | "deactivate" | null>(null);
  const [selectedUser, setSelectedUser] = useState<ReportGenUser | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const toast = useToast();

  const usersQuery = useQuery({
    ...stationUsersQuery(organizationId),
    enabled: !!organizationId,
  });
  const users = usersQuery.data ?? [];
  // isFetching keeps the loading state visible during explicit refreshes,
  // matching the old fetchAllUsers behavior.
  const isLoading = (!!organizationId && usersQuery.isPending) || usersQuery.isFetching;

  // react-query v5 has no onError — surface fetch failures as before.
  useEffect(() => {
    if (!usersQuery.error) return;
    const message = isAxiosError(usersQuery.error)
      ? usersQuery.error.response?.data?.error
      : null;
    toast.error(message || "Failed to fetch users");
  }, [usersQuery.error, toast]);

  // Invalidates both roster and directory variants for this station.
  const fetchAllUsers = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: usersKeys.station(organizationId),
    });
  }, [organizationId]);

  const handleResendInvite = useCallback(
    async (user: ReportGenUser) => {
      setResendingInviteEmail(user.email);
      try {
        if (user.id) {
          await profileClient.post(
            `/v1/stations/${organizationId}/users/${user.id}/invite`,
            { userId: user.id },
          );
        } else {
          await profileClient.post(
            `/v1/stations/${organizationId}/users/invite`,
            { email: user.email },
          );
        }
        toast.success(`Invitation resent to ${user.email}`);
      } catch (error) {
        const message = isAxiosError(error) ? error.response?.data?.error : null;
        toast.error(message || "Failed to resend invitation");
      } finally {
        setResendingInviteEmail(null);
      }
    },
    [organizationId, toast],
  );

  const handleResetPassword = useCallback(
    async (user: ReportGenUser) => {
      if (!user.id) {
        toast.error("Cannot reset password: User ID is missing.");
        return;
      }
      setResettingPasswordEmail(user.email);
      try {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${user.id}/password-reset`,
        );
        toast.success(`Password reset email sent to ${user.email}`);
      } catch (error) {
        const message = isAxiosError(error) ? error.response?.data?.error : null;
        toast.error(message || "Failed to send password reset");
      } finally {
        setResettingPasswordEmail(null);
      }
    },
    [organizationId, toast],
  );

  const handleStatusChangeRequest = useCallback(
    (user: ReportGenUser, action: "activate" | "deactivate") => {
      setSelectedUser(user);
      setStatusAction(action);
      setStatusDialogOpen(true);
    },
    [],
  );

  const closeStatusDialog = useCallback(() => {
    if (isUpdatingStatus) return;
    setStatusDialogOpen(false);
    setStatusAction(null);
    setSelectedUser(null);
  }, [isUpdatingStatus]);

  const handleStatusConfirm = useCallback(async () => {
    if (!selectedUser || !statusAction) return;
    setIsUpdatingStatus(true);
    try {
      const payload = { email: selectedUser.email };
      if (statusAction === "activate") {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${selectedUser.id}/activate`,
          payload,
        );
        toast.success("User re-activated successfully.");
      } else {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${selectedUser.id}/deactivate`,
          payload,
        );
        toast.success("User deactivated successfully.");
      }
      await fetchAllUsers();
    } catch (error) {
      const message = isAxiosError(error) ? error.response?.data?.error : null;
      toast.error(message || "Failed to update user status.");
    } finally {
      setIsUpdatingStatus(false);
      setStatusDialogOpen(false);
      setStatusAction(null);
      setSelectedUser(null);
    }
  }, [selectedUser, statusAction, organizationId, toast, fetchAllUsers]);

  const statusUpdatingEmail = isUpdatingStatus && selectedUser ? selectedUser.email : null;

  return {
    users,
    isLoading,
    searchTerm,
    setSearchTerm,
    resendingInviteEmail,
    resettingPasswordEmail,
    statusUpdatingEmail,
    statusDialogOpen,
    statusAction,
    selectedUser,
    isUpdatingStatus,
    refresh: fetchAllUsers,
    handleResendInvite,
    handleResetPassword,
    handleStatusChangeRequest,
    closeStatusDialog,
    handleStatusConfirm,
  };
};
